import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { MockServiceCapabilities, UpdateConfig } from 'types/rest/mockServiceTypes';
import { useStore } from 'react-redux';
import _ from 'lodash';
import Switcher from 'components/ui/switcher/Switcher';

interface UpdateServiceMockModalProps {
  updateAction: (reconfigureConfig: UpdateConfig) => void;
  mockServiceCapabilities: MockServiceCapabilities;
  templateId?: number;
}

/**
 * Modal shown with further config options depending on what capabilities virtual service has.
 * @param props
 * @constructor
 */
const UpdateServiceMockModal = (props: UpdateServiceMockModalProps) => {
  const { t } = useTranslation('mockServices');
  const store = useStore();

  const initialConfig = {
    keepBlazeData: true,
    updateAppliedTemplate: false,
  } as UpdateConfig;

  // Use state when Applied Template will be implemented...
  const [reconfigureConfig, setReconfigureConfig] = useState(initialConfig);
  const [templateName, setTemplateName] = useState('');
  const { mockServiceCapabilities } = props;

  useEffect(() => {
    if (!_.isNil(props.templateId)) {
      const tmplName = getTemplateName(props.templateId);
      setTemplateName(tmplName);
    }
  }, [props.templateId]);

  /** Gets template name from the redux store. */
  const getTemplateName = (templateId: number) => {
    //Get list of templates from redux
    const templates = store.getState().mockServiceTemplates.mockServiceTemplates;
    const template = templates.find((t: any) => t.id === templateId || t.serviceMockTemplateId === templateId);

    if (template) {
      return template.name;
    } else {
      return null;
    }
  };

  const applyHandler = () => {
    if (isBlazeDataModal || isAppliedTemplateModal) {
      props.updateAction({ updateAppliedTemplate: true, keepBlazeData: false });
    } else {
      //Otherwise just re-use local reconfigure config configured by set of toggles
      props.updateAction(reconfigureConfig);
    }
  };

  //When user clicks cancel, use defaults
  const cancelHandler = () => {
    props.updateAction(initialConfig);
  };

  const isBlazeDataModal =
    mockServiceCapabilities.isBlazeData === true && mockServiceCapabilities.appliedTemplate === false;
  const isAppliedTemplateModal =
    mockServiceCapabilities.isBlazeData === false && mockServiceCapabilities.appliedTemplate === true;
  const isMultiModal = mockServiceCapabilities.isBlazeData === true && mockServiceCapabilities.appliedTemplate === true;

  const getHeaderTitle = () => {
    if (isBlazeDataModal) {
      return t('updateModal.title.blazeDataUpdate');
    } else if (isAppliedTemplateModal) {
      return t('updateModal.title.appliedTemplateUpdate');
    }
    return t('updateModal.title.multiUpdate');
  };

  const getMultiBodyDom = () => {
    return (
      <div>
        <div className={'mb-5'}>
          {/* Template Update settings */}
          <Switcher
            size={'lg'}
            label="Update Template"
            checked={reconfigureConfig.updateAppliedTemplate}
            onChange={() => {
              setReconfigureConfig({
                ...reconfigureConfig,
                updateAppliedTemplate: !reconfigureConfig.updateAppliedTemplate,
              });
            }}
          />
          <p
            className={'mt-4 text-muted'}
            dangerouslySetInnerHTML={{ __html: t('updateModal.body.templateUpdate', { templateName: templateName }) }}
          ></p>
        </div>

        {/* Blaze Data Update settings */}
        <div>
          <Switcher
            size={'lg'}
            label="Keep Data"
            checked={reconfigureConfig.keepBlazeData}
            onChange={() => {
              setReconfigureConfig({
                ...reconfigureConfig,
                keepBlazeData: !reconfigureConfig.keepBlazeData,
              });
            }}
          />
          <p className={'mt-4 text-muted'}>{t('updateModal.body.blazeDataToggle')}</p>
        </div>
      </div>
    );
  };

  return (
    <Modal
      id="qa-modal-reconfigure-action"
      show={true}
      centered={true}
      backdrop={true}
      onHide={cancelHandler}
      size="sm"
    >
      <Modal.Header closeButton id="qa-modal-select-sv-creds-btn-close-icon">
        <h5 className="modal-title">{getHeaderTitle()}</h5>
      </Modal.Header>
      <Modal.Body>
        {isBlazeDataModal && <p> {t('updateModal.body.blazeDataUpdate')}</p>}
        {isAppliedTemplateModal && (
          <p
            dangerouslySetInnerHTML={{ __html: t('updateModal.body.templateUpdate', { templateName: templateName }) }}
          ></p>
        )}
        {isMultiModal && getMultiBodyDom()}
      </Modal.Body>
      <Modal.Footer>
        {!isMultiModal && (
          <Button onClick={cancelHandler} id={'qa-modal-select-sv-creds-btn-cancel'} variant="link">
            {t('common:confirmations.no')}
          </Button>
        )}
        <Button onClick={applyHandler} id={'qa-modal-select-sv-creds-btn-apply'} variant="primary">
          {isMultiModal ? t('common:actions.done') : t('common:confirmations.yes')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateServiceMockModal;
